import React, { useState } from "react";
import { Button, Spinner } from "@fluentui/react-components";
import axios from "axios";
import { Base_Url, getAuthToken } from "../services/firebase";
import { notify } from "./AppNotifications";
import { exportToCsv, csvFileName } from "./CsvExportUtil";

// The Export Filtered / Export All pair, shared by every list screen.
//
// One component rather than a copy per screen: the escaping, the audit call,
// the empty-list case and the wording of the failure messages all have to
// behave the same way on twenty-one screens, and the existing report screens
// show what happens otherwise - the same export helper duplicated six times.
//
// A screen supplies three things:
//
//   getFilteredRows()  the rows currently on screen, after search and filters
//   getAllRows()       every row; may return a promise, because some screens
//                      page from Firestore and have to fetch the rest first
//   mapRow(row)        one row as a flat object, keys becoming the headers
//
export default function ExportCsvButtons({
    screenName,
    getFilteredRows,
    getAllRows,
    mapRow,
}) {
    const [busy, setBusy] = useState(null); // "filtered" | "all" | null

    // Best-effort. The file has already been written by the time this runs, so
    // a logging failure must never be reported as a failed export - it would
    // send staff looking for a download that is sitting in their folder.
    async function recordExport(scope, rowCount) {
        try {
            const token = await getAuthToken();
            await axios.post(
                `${Base_Url}/api/logExport`,
                { screen: screenName, scope, rowCount },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (err) {
            console.error("logExport failed", err);
        }
    }

    async function run(scope) {
        if (busy) return;
        setBusy(scope);
        try {
            const rows = scope === "all" ? await getAllRows() : getFilteredRows();
            const list = Array.isArray(rows) ? rows : [];

            if (list.length === 0) {
                notify(
                    scope === "all"
                        ? "There is nothing to export on this screen yet."
                        : "No rows match the current search and filters.",
                    "warning",
                    "Nothing to export"
                );
                return;
            }

            const suffix = scope === "all" ? "" : "-filtered";
            const count = exportToCsv(list.map(mapRow), csvFileName(screenName) + suffix);

            notify(
                `${count} ${count === 1 ? "row" : "rows"} exported.`,
                "success",
                "Export ready"
            );
            recordExport(scope, count);
        } catch (err) {
            console.error("CSV export failed", err);
            notify(
                "The export could not be created. Please try again, and report it if it keeps happening.",
                "error",
                "Export failed"
            );
        } finally {
            setBusy(null);
        }
    }

    return (
        <div className={"d-flex"} style={{ gap: 8 }}>
            <Button
                appearance="secondary"
                shape="circular"
                disabled={!!busy}
                onClick={() => run("filtered")}
                icon={busy === "filtered" ? <Spinner size="tiny" /> : undefined}
            >
                {busy === "filtered" ? "Exporting…" : "Export Filtered"}
            </Button>
            <Button
                appearance="secondary"
                shape="circular"
                disabled={!!busy}
                onClick={() => run("all")}
                icon={busy === "all" ? <Spinner size="tiny" /> : undefined}
            >
                {busy === "all" ? "Exporting…" : "Export All"}
            </Button>
        </div>
    );
}
